import {
  performedSetSchema,
  restEventSchema,
  workoutLogSchema,
  type PerformedSet,
  type SetPrescription,
  type WorkoutLog,
} from '@cadencia/contracts';
import {
  countVolume,
  E1RM_FORMULA_VERSION,
  e1rmEpleyRir,
  type ExerciseHistory,
  type ExerciseSessionSummary,
} from '@cadencia/domain';
import { db } from '../db.js';
import { nowIso } from '../ids.js';
import { newSyncColumns, putNewRows, putRow, type WriteContext } from '../writes.js';
import type { SessionDetail } from './trainingRepo.js';
import { updateSession } from './trainingRepo.js';
import { diaLocalDe } from '../../lib/dates.js';

/** Repositorio de ejecucion: lo que realmente ocurre en el gimnasio. */

export async function getActiveWorkout(userId: string): Promise<WorkoutLog | undefined> {
  const logs = await db.workoutLogs.where('userId').equals(userId).toArray();
  return logs.find((log) => log.deletedAt === null && log.status === 'in_progress');
}

/**
 * El parte de esa sesion. Como mucho hay uno vivo (ver `startWorkout`).
 *
 * El desempate NO es decorativo: hasta el 06/09/2026 `startWorkout` creaba un
 * parte nuevo cada vez que se volvia a una sesion ya terminada, asi que puede
 * haber bases con varios. `find` devolvia el primero que diera Dexie, que no es
 * un orden prometido por nadie: la misma pantalla podia enseñar un dia las
 * series y otro dia ninguna.
 *
 * Ante varios gana el que este EN CURSO —solo puede haber uno entrenandose— y
 * si no, el mas antiguo: el duplicado nacia vacio al revisitar, asi que el
 * primero es el que tiene las series de verdad. El `id` desempata para que la
 * respuesta no dependa nunca del orden de lectura.
 */
export async function getWorkoutBySession(sessionId: string): Promise<WorkoutLog | undefined> {
  const logs = (await db.workoutLogs.where('sessionId').equals(sessionId).toArray()).filter(
    (log) => log.deletedAt === null,
  );
  if (logs.length <= 1) return logs[0];

  return [...logs].sort((a, b) => {
    if (a.status === 'in_progress' !== (b.status === 'in_progress')) {
      return a.status === 'in_progress' ? -1 : 1;
    }
    return a.startedAt.localeCompare(b.startedAt) || a.id.localeCompare(b.id);
  })[0];
}

/**
 * Empieza la sesion: congela la prescripcion en `planSnapshot` y marca un
 * bloqueo suave en la sesion. Editar la plantilla despues NO alterara este
 * historial (requisito §61).
 */
export async function startWorkout(
  ctx: WriteContext,
  detail: SessionDetail,
  readinessCheckinId: string | null,
): Promise<WorkoutLog> {
  // IDEMPOTENTE, y no solo mientras se entrena.
  //
  // La condicion era `existing.status === 'in_progress'`, asi que volver a una
  // sesion YA TERMINADA creaba un segundo parte para la misma sesion: dos filas
  // que sincronizan, un historial partido en dos y `getWorkoutBySession`
  // devolviendo cualquiera de las dos. Y no hacia falta nada raro para
  // provocarlo: entrar otra vez en la sesion de ayer desde el calendario.
  //
  // Devolver el que hay es todo lo que hace falta. No se reabre —el parte
  // terminado conserva su `completed` y la sesion su `completed`—, no se borra
  // nada y la pantalla ya sabe tratarlo: sin series pendientes enseña «Sesion
  // completada» y ofrece ir al cierre.
  const existing = await getWorkoutBySession(detail.session.id);
  if (existing) return existing;

  const planSnapshot = {
    sessionName: detail.session.name,
    objective: detail.session.objective,
    scheduledDate: detail.session.scheduledDate,
    estimatedMinutes: detail.session.estimatedMinutes,
    exercises: detail.exercises.map((item) => ({
      sessionExerciseId: item.sessionExercise.id,
      exerciseSlug: item.sessionExercise.exerciseSlug,
      equipmentId: item.sessionExercise.equipmentId,
      orderIndex: item.sessionExercise.orderIndex,
      role: item.sessionExercise.role,
      restSeconds: item.sessionExercise.targetRestSeconds,
      sets: item.prescriptions.map((prescription) => ({
        setIndex: prescription.setIndex,
        setType: prescription.setType,
        repsMin: prescription.targetRepsMin,
        repsMax: prescription.targetRepsMax,
        rir: prescription.targetRir,
      })),
    })),
  };

  const log = workoutLogSchema.parse({
    ...newSyncColumns(ctx),
    sessionId: detail.session.id,
    startedAt: nowIso(),
    endedAt: null,
    readinessCheckinId,
    planSnapshot,
    sessionDifficulty: null,
    sessionQuality: null,
    totalEffectiveSets: 0,
    status: 'in_progress',
  });

  await putNewRows(ctx, [{ table: 'workoutLogs', row: log }]);
  await updateSession(ctx, detail.session, {
    status: 'in_progress',
    lockedByDevice: ctx.deviceId,
    lockedAt: nowIso(),
  });

  return log;
}

export async function listPerformedSets(workoutLogId: string): Promise<PerformedSet[]> {
  const sets = await db.performedSets.where('workoutLogId').equals(workoutLogId).toArray();
  return sets
    .filter((set) => set.deletedAt === null)
    .sort((a, b) => a.performedAt.localeCompare(b.performedAt));
}

export interface LogSetInput {
  workoutLog: WorkoutLog;
  sessionExerciseId: string;
  prescription: SetPrescription;
  exerciseSlug: string;
  equipmentId: string | null;
  loadKg: number;
  reps: number;
  rir: number;
  recommendedLoadKg: number | null;
  restPlannedSeconds: number;
}

/**
 * Registra una serie. Escribe en local y encola; NUNCA espera a la red
 * (requisito §73). El e1RM se deriva aqui y se guarda con su version de formula.
 */
export async function logPerformedSet(
  ctx: WriteContext,
  input: LogSetInput,
): Promise<PerformedSet> {
  const performedAt = nowIso();
  const e1rm = e1rmEpleyRir({ loadKg: input.loadKg, reps: input.reps, rir: input.rir });

  const row = performedSetSchema.parse({
    ...newSyncColumns(ctx),
    workoutLogId: input.workoutLog.id,
    sessionExerciseId: input.sessionExerciseId,
    setPrescriptionId: input.prescription.id,
    exerciseSlug: input.exerciseSlug,
    equipmentId: input.equipmentId,
    setIndex: input.prescription.setIndex,
    setType: input.prescription.setType,
    loadKg: input.loadKg,
    reps: input.reps,
    rir: input.rir,
    e1rmKg: e1rm,
    formulaVersion: E1RM_FORMULA_VERSION,
    recommendedLoadKg: input.recommendedLoadKg,
    wasOverride:
      input.recommendedLoadKg !== null && Math.abs(input.recommendedLoadKg - input.loadKg) > 0.01,
    performedAt,
  });

  await putNewRows(ctx, [{ table: 'performedSets', row }]);
  return row;
}

/** Registra el descanso real que siguio a una serie. */
export async function logRestEvent(
  ctx: WriteContext,
  input: {
    workoutLogId: string;
    afterPerformedSetId: string | null;
    plannedSeconds: number;
    actualSeconds: number;
    startedAt: string;
  },
): Promise<void> {
  const row = restEventSchema.parse({ ...newSyncColumns(ctx), ...input });
  await putNewRows(ctx, [{ table: 'restEvents', row }]);
}

export async function finishWorkout(
  ctx: WriteContext,
  log: WorkoutLog,
  evaluation: { difficulty: number; quality: number },
): Promise<WorkoutLog> {
  const sets = await listPerformedSets(log.id);

  // La cuenta la hace el DOMINIO. Aqui habia `sets.filter(s => s.setType !==
  // 'drop').length`, que no es ninguna definicion de «serie efectiva»: tiraba
  // los drops en vez de contarlos a mitad y daba a un rest-pause el mismo peso
  // que a una serie normal. `countVolume` es la unica que sabe lo que pesa cada
  // tipo, y reimplementarlo aqui era la segunda copia de la regla (BUG-005).
  const effective = countVolume(
    sets.map((set) => ({ exerciseSlug: set.exerciseSlug, setType: set.setType })),
  ).effectiveSets;

  const updated = await putRow(
    ctx,
    'workoutLogs',
    workoutLogSchema.parse({
      ...log,
      endedAt: nowIso(),
      sessionDifficulty: evaluation.difficulty,
      sessionQuality: evaluation.quality,
      totalEffectiveSets: effective,
      status: 'completed',
    }),
  );

  if (log.sessionId !== null) {
    const session = await db.trainingSessions.get(log.sessionId);
    if (session) {
      await updateSession(ctx, session, {
        status: 'completed',
        lockedByDevice: null,
        lockedAt: null,
      });
    }
  }

  return updated;
}

/**
 * Historial de un ejercicio en una instancia concreta de equipamiento.
 * Las cargas NUNCA se trasladan entre maquinas distintas (ADR-014).
 */
export async function getExerciseHistory(
  userId: string,
  exerciseSlug: string,
  equipmentId: string | null,
  maxSessions = 6,
): Promise<ExerciseHistory> {
  const all = await db.performedSets.where('exerciseSlug').equals(exerciseSlug).toArray();
  const relevant = all.filter(
    (set) =>
      set.userId === userId &&
      set.deletedAt === null &&
      (set.equipmentId ?? null) === equipmentId,
  );

  const byLog = new Map<string, ExerciseSessionSummary>();
  for (const set of relevant.sort((a, b) => a.performedAt.localeCompare(b.performedAt))) {
    // El dia local se resuelve AQUI, que es donde se conoce el huso. Dentro del
    // dominio no se puede (ADR-007), y recortar el ISO daria el dia en UTC.
    const entry = byLog.get(set.workoutLogId) ?? {
      at: set.performedAt,
      localDate: diaLocalDe(set.performedAt),
      sets: [],
    };
    entry.sets.push({
      setIndex: set.setIndex,
      loadKg: set.loadKg,
      reps: set.reps,
      rir: set.rir,
    });
    byLog.set(set.workoutLogId, entry);
  }

  const sessions = [...byLog.values()]
    .sort((a, b) => a.at.localeCompare(b.at))
    .slice(-maxSessions);

  return { sessions };
}

/** Series efectivas ya registradas hoy, para estimar la fatiga acumulada. */
/**
 * ¿Ha registrado este usuario alguna serie, alguna vez?
 *
 * Decide si se enseña la guía de la primera sesión (§7). Se mira en las series
 * y no en los partes: abrir una sesión y salir sin registrar nada crea un parte
 * vacío, y eso no es haber entrenado.
 */
export async function hasAnyPerformedSet(userId: string): Promise<boolean> {
  const alguna = await db.performedSets
    .where('userId')
    .equals(userId)
    .filter((fila) => fila.deletedAt === null)
    .first();
  return alguna !== undefined;
}

export async function countSetsToday(workoutLogId: string): Promise<number> {
  return (await listPerformedSets(workoutLogId)).length;
}
